import { Component, OnInit, OnDestroy } from '@angular/core';
import { SessionService } from '../../../services/session.service';
import { PlatformApiService } from '../../../services/platform-api.service';
import { ActivatedRoute } from '@angular/router';
import { ThemeManagerService } from 'src/app/services/theme-manager.service';
import { Account } from '../../../models/AccountModel';
import { AccountAssessment, AssessmentStatus } from '../../../models/AssessmentModel';
import { OidcSbamService } from 'src/app/services/oidc.service';
import { UserIdleService } from 'angular-user-idle';
import { ConfirmationComponent } from 'src/app/shared/confirmation.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'cw-ci-sbam-layout',
  templateUrl: './sbam-layout.component.html',
  styleUrls: ['./sbam-layout.component.scss']
})
export class SbamLayoutComponent implements OnInit, OnDestroy {
  state = 'LOADING';
  requestedState:string;
  requestedCompanyId:number;
  requestedId:number;
  partnerKey:string;
  loadingAccount = false;
  dialogTitle:string = undefined;
  dialogMessage:string = undefined;
  selectedAssessment:AccountAssessment;
  account:Account;

  private observer:MutationObserver;

  constructor(private _userIdle: UserIdleService, private dialog: MatDialog, public _sessionService: SessionService, public themeManager: ThemeManagerService, private _platformApi: PlatformApiService, private _activatedRoute: ActivatedRoute, private _oidcSbamService: OidcSbamService) {
    const self = this;
    SessionService.overrideSessionTimeoutAction = () => {
      window.parent.postMessage(JSON.stringify({namespace: 'cw-identify', event: 'SESSIONTIMEOUT'}), '*');

      self.dialogTitle = 'TIMEOUT';
      self.dialogMessage = 'Your session has timed out due to inactivity. Please reopen the assessment.';
      self.requestedState='ERRORSTATE';
      self.state = self.requestedState;
    };
  }

  ngOnInit() {
    this.trackHeight();

    this.themeManager.setThemeClass('sbam-theme');

    this.partnerKey = this._activatedRoute.snapshot.data.partnerKey;

    if (this.partnerKey != 'sbam') {
      this.dialogTitle = 'UNAUTHORIZED';
      this.dialogMessage = 'This user is not authorized to access this application';
      this.requestedState='ERRORSTATE';
      this.state = this.requestedState;
    } else {
      this.requestedState = 'LOADING';

      this._activatedRoute.params.subscribe(params => {
        console.log('Route', this._activatedRoute.routeConfig.path, params);

        if (Object.keys(params).includes('companyId')) {
          this.requestedCompanyId=params.companyId;
        }

        if (Object.keys(params).includes('id')) {
          this.requestedId=params.id;
        }
      })

      const idToken = this._oidcSbamService.setSigningState(window.location.href);

      if (idToken.payload.iss && idToken.header.jku) {
        this._oidcSbamService.getManager().settings.metadata = {
          issuer: idToken.payload.iss,
          jwks_uri: idToken.header.jku,
        }
      }

      this._oidcSbamService.getManager().signinRedirectCallback().then(user => {
        this._platformApi.getCredentials().subscribe(creds => {
          this._sessionService.setApiCredentials(creds);
          this.account = this._sessionService.getCurrentAccount();
          this.updateState();
          this.startIdleTimer();
        }, err => {
          this.dialogTitle = 'UNAUTHORIZED';
          this.dialogMessage = 'This user is not authorized to access this application';
          this.requestedState='ERRORSTATE';
          this.state = this.requestedState;
          console.log('Error getting creds.', err);
        });
      }, err => {
        this.dialogTitle = 'UNAUTHORIZED';
        this.dialogMessage = 'An error occurred during the sign in process';
        this.requestedState='ERRORSTATE';
        this.state = this.requestedState;
        console.log('Error with oidc login.', err);
      });
    };
  }

  startIdleTimer() {
      // Start watching for user inactivity.
      this._userIdle.startWatching();

      // Start watching when user idle is starting.
      let timeoutPrompt;

      const timerSubscription = this._userIdle.onTimerStart().subscribe(count => {
        if (!timeoutPrompt) {
          timeoutPrompt = this.dialog.open(ConfirmationComponent, {
            panelClass: 'flush-dialog',
            data: {
              promptMessage: ['Your session is about to timeout due to inactivity. Do you want to stay logged in?'],
              promptTitle: 'Session Timeout',
              showClose: false,
              buttons: [{label:'Stay Logged In', value:true, color:'primary'}],
              height: 'auto'
            },
            disableClose: true
          });
          timeoutPrompt.afterClosed().subscribe(resp => {
            if (resp == true) {
              this._userIdle.resetTimer();
            }
            timeoutPrompt = undefined;
          });
        }
      });

      // Start watch when time is up.
      this._userIdle.onTimeout().subscribe(() => {
        if (timeoutPrompt) {
          timeoutPrompt.close();
        }

        timerSubscription.unsubscribe();
        this._userIdle.stopWatching();
        this._sessionService.handleSessionTimeout();
      });

      // this._userIdle.ping$.subscribe(() => this._sessionService.refreshSessionData());
  }

  trackHeight() {
    const targetNode = document.getElementById('sbamWrapper');
    let currentHeight = targetNode.scrollHeight;
    const config = { attributes: false, childList: true, subtree: true };

    const callback = (mutationsList) => {
      for(const mutation of mutationsList) {
        if (currentHeight !== targetNode.scrollHeight) {

          currentHeight = targetNode.scrollHeight;
          window.parent.postMessage(JSON.stringify({namespace: 'cw-identify', event: 'HEIGHTCHANGE', height: currentHeight}), '*');
        }
      }
    };

    this.observer = new MutationObserver(callback);

    this.observer.observe(targetNode, config);
  }

  ngOnDestroy() {
    this.observer.disconnect();
  }

  updateState() {
    if (this._sessionService.getCurrentAccount()) {
      if (this.requestedCompanyId && this.requestedCompanyId > 0) {
        if (!this.loadingAccount) {
          this.loadingAccount = true;
          if (this._sessionService) {
            this._platformApi.getAccountAssessment(this.account.id, this.requestedCompanyId, this.requestedId).subscribe(assessment => {
              if (assessment.status == AssessmentStatus.COMPLETED) {
                this.state = 'REPORT';
              } else {
                this.state = 'ASSESSMENT';
              }
              this.selectedAssessment = assessment;
            }, err => {
              this.dialogTitle = 'NOT FOUND';
              this.dialogMessage = 'Could not find the specified assessment';
              this.requestedState='ERRORSTATE';
              this.state = this.requestedState;
            })
          }
        }
      } else {
        this.state = this.requestedState;
      }
    } else {
      console.log('No current account');
    }
  }

  onStateChange(event) {
    switch(event.state) {
      case 'COMPLETEDASSESSMENT':
        this.selectedAssessment = event.completedAssessment;
        this.state = 'COMPLETEDASSESSMENT';
        window.parent.postMessage(JSON.stringify({namespace: 'cw-identify', event: 'COMPLETEDASSESSMENT'}), '*');
        break;
      case 'NEXTPAGE':
      case 'PREVPAGE':
          window.parent.postMessage(JSON.stringify({namespace: 'cw-identify', event: 'PAGECHANGE'}), '*');
        break;
    }
  }
}
